import { Falling, PlayerTag } from '@/components'
import type { World } from '@/core/ecs/World'
import { Events } from '@/game/events'
import { EventsResource } from '@/game/resources'

/** Impulse above which a landing hit is worth freezing the frame for. */
const HIT_STOP_THRESHOLD = 13
/**
 * Minimum gap between two freezes, in seconds. Long enough that a six-way
 * brawl cannot chain them into permanent slow motion, short enough that the
 * exchanges a duel is made of each get their own.
 */
const HIT_STOP_REFRACTORY = 0.45
/** Speed of the simulation clock while frozen. */
const HIT_STOP_SCALE = 0.12

/**
 * Hit stop: a few frames of near-frozen time on a heavy landing.
 *
 * This is the cheapest way to make an impact feel like it has mass. Without
 * it a knockback is just a number changing; with it the game visibly braces
 * on impact and the player *feels* that the hit connected. Deliberately very
 * short — long enough to register, too short to interfere with control.
 *
 * It scales the simulation clock of whoever runs the simulation: the browser
 * offline, the server online, where every player sees the same freeze.
 */
export class HitStop {
  private freeze = 0
  /** Time until another hit may freeze the game. */
  private cooldown = 0

  /**
   * Reads the hits in the world's event queue — everything since the queue
   * was last drained — and returns the clock's time scale for what follows.
   * Call once per drain, with the real (unscaled) time that passed.
   */
  update(world: World, rawDt: number): number {
    let strongest = 0
    for (const hit of world.resource(EventsResource).read(Events.Hit)) {
      if (hit.kind === 'bump') continue
      strongest = Math.max(strongest, hit.power)
    }

    // A refractory period between freezes. Scaling each freeze down for a crowd
    // was not enough, because a crowd does not land *weaker* hits, it lands
    // *more* of them: measured, six fighters spent 14.4 % of the fight in
    // 0.12× time against 5.7 % for a duel — the game was genuinely running slow
    // and it read as lag rather than as impact. Emphasis needs contrast, so
    // hitstop is worth nothing if it is always on.
    this.cooldown = Math.max(0, this.cooldown - rawDt)

    if (strongest >= HIT_STOP_THRESHOLD && this.cooldown <= 0) {
      this.cooldown = HIT_STOP_REFRACTORY
      // Normalised against the same 40 m/s range as every other feedback
      // channel, so the top of the curve is a real hit and not an early
      // saturation point. 60 ms for a graze, 170 ms for a launch.
      const scaled = Math.min(1, (strongest - HIT_STOP_THRESHOLD) / 40)
      // With a crowd on screen, a full freeze per hit would leave the game
      // stuttering constantly — Smash cuts hitstop the same way as the player
      // count rises. Six fighters get 0.775 of a duel's freeze.
      const alive = Math.max(2, world.query([PlayerTag], { none: [Falling] }).entities().length)
      const crowd = Math.max(0.7, 1 - 0.05625 * (alive - 2))
      this.freeze = Math.max(this.freeze, (0.06 + scaled * 0.11) * crowd)
    }

    if (this.freeze > 0) {
      this.freeze = Math.max(0, this.freeze - rawDt)
      return HIT_STOP_SCALE
    }
    return 1
  }
}
